import { doc, setDoc, updateDoc, getDoc, arrayUnion } from "firebase/firestore";

const getActorName = (user = {}) => user?.displayName || user?.name || "Unknown";
const getActorEmail = (user = {}) => user?.email || "";

const buildHistoryEntry = ({
  action,
  dispatchStatus,
  timestamp,
  user,
  remarks = ""
}) => ({
  action,
  actionAt: timestamp,
  actionBy: getActorName(user),
  actionByEmail: getActorEmail(user),
  dispatchStatus: dispatchStatus || "",
  remarks
});

export const createDispatchAudit = async (
  db,
  {
    dispatchId,
    dispatchData,
    user,
    historyEvent
  }
) => {
  const timestamp = historyEvent?.timestamp || new Date().toISOString();
  const actorName = getActorName(user);
  const actorEmail = getActorEmail(user);
  const initialAction = historyEvent?.action || 'moc_created';
  const initialStatus = historyEvent?.dispatchStatus || dispatchData?.dispatchStatus || '';

  await setDoc(doc(db, "dispatch_audit", dispatchId), {
    dispatchId,
    orderId: dispatchData?.orderId || "",
    orderNumber: dispatchData?.orderNumber || "",
    mocNo: dispatchData?.mocNo || "",
    dispatchStatus: dispatchData?.dispatchStatus || "",
    createdAt: timestamp,
    createdBy: actorName,
    createdByEmail: actorEmail,
    updatedAt: timestamp,
    updatedBy: actorName,
    updatedByEmail: actorEmail,
    mocCreatedAt: initialAction === 'moc_created' ? timestamp : '',
    mocCreatedBy: initialAction === 'moc_created' ? actorName : '',
    mocUpdatedAt: '',
    mocUpdatedBy: '',
    mocSubmittedAt: initialAction === 'moc_submitted' ? timestamp : '',
    mocSubmittedBy: initialAction === 'moc_submitted' ? actorName : '',
    mocApprovedAt: initialAction === 'moc_approved_by_ops_head' ? timestamp : '',
    mocApprovedBy: initialAction === 'moc_approved_by_ops_head' ? actorName : '',
    mocRejectedAt: initialAction === 'moc_rejected_by_ops_head' ? timestamp : '',
    mocRejectedBy: initialAction === 'moc_rejected_by_ops_head' ? actorName : '',
    financeUpdatedAt: initialAction === 'finance_updated' ? timestamp : '',
    financeUpdatedBy: initialAction === 'finance_updated' ? actorName : '',
    financeApprovedAt: initialAction === 'finance_approved' ? timestamp : '',
    financeApprovedBy: initialAction === 'finance_approved' ? actorName : '',
    financeRejectedAt: initialAction === 'finance_rejected' ? timestamp : '',
    financeRejectedBy: initialAction === 'finance_rejected' ? actorName : '',
    financeHoldAt: initialAction === 'finance_hold' ? timestamp : '',
    financeHoldBy: initialAction === 'finance_hold' ? actorName : '',
    packingQueuedAt: initialAction === 'packing_queued' ? timestamp : '',
    packingQueuedBy: initialAction === 'packing_queued' ? actorName : '',
    packingScheduledAt: ['packing_scheduled', 'packing_schedule_updated'].includes(initialAction) ? timestamp : '',
    packingScheduledBy: ['packing_scheduled', 'packing_schedule_updated'].includes(initialAction) ? actorName : '',
    packedReadyAt: initialAction === 'packed_ready' ? timestamp : '',
    packedReadyBy: initialAction === 'packed_ready' ? actorName : '',
    dcCreatedAt: initialAction === 'dc_created' ? timestamp : '',
    dcCreatedBy: initialAction === 'dc_created' ? actorName : '',
    dcUpdatedAt: initialAction === 'dc_updated' ? timestamp : '',
    dcUpdatedBy: initialAction === 'dc_updated' ? actorName : '',
    dcSubmittedAt: initialAction === 'dc_submitted' ? timestamp : '',
    dcSubmittedBy: initialAction === 'dc_submitted' ? actorName : '',
    invoiceSubmittedAt: initialAction === 'invoice_submitted' ? timestamp : '',
    invoiceSubmittedBy: initialAction === 'invoice_submitted' ? actorName : '',
    qcUpdatedAt: initialAction === 'qc_updated' ? timestamp : '',
    qcUpdatedBy: initialAction === 'qc_updated' ? actorName : '',
    dispatchConfirmedAt: initialAction === 'dispatch_confirmed' ? timestamp : '',
    dispatchConfirmedBy: initialAction === 'dispatch_confirmed' ? actorName : '',
    deliveryConfirmedAt: initialAction === 'delivery_confirmed' ? timestamp : '',
    deliveryConfirmedBy: initialAction === 'delivery_confirmed' ? actorName : '',
    history: [
      buildHistoryEntry({
        action: initialAction,
        dispatchStatus: initialStatus,
        timestamp,
        user,
        remarks: historyEvent?.remarks || ""
      })
    ]
  }, { merge: true });
};

export const updateDispatchAudit = async (
  db,
  {
    dispatchId,
    dispatchData = {},
    user,
    historyEvent
  }
) => {
  const timestamp = historyEvent?.timestamp || new Date().toISOString();
  const actorName = getActorName(user);
  const actorEmail = getActorEmail(user);
  const auditRef = doc(db, "dispatch_audit", dispatchId);
  const auditSnap = await getDoc(auditRef);
  const existingAudit = auditSnap.exists() ? auditSnap.data() : {};

  if (!auditSnap.exists()) {
    await createDispatchAudit(db, {
      dispatchId,
      dispatchData,
      user,
      historyEvent
    });
    return;
  }

  const fieldUpdates = {
    dispatchStatus: dispatchData?.dispatchStatus || "",
    updatedAt: timestamp,
    updatedBy: actorName,
    updatedByEmail: actorEmail,
    history: arrayUnion(buildHistoryEntry({
      action: historyEvent?.action || 'moc_updated',
      dispatchStatus: historyEvent?.dispatchStatus || dispatchData?.dispatchStatus || "",
      timestamp,
      user,
      remarks: historyEvent?.remarks || ""
    }))
  };

  if (dispatchData?.orderId) fieldUpdates.orderId = dispatchData.orderId;
  if (dispatchData?.orderNumber) fieldUpdates.orderNumber = dispatchData.orderNumber;
  if (dispatchData?.mocNo) fieldUpdates.mocNo = dispatchData.mocNo;

  switch (historyEvent?.action) {
    case 'moc_updated':
      fieldUpdates.mocUpdatedAt = timestamp;
      fieldUpdates.mocUpdatedBy = actorName;
      break;
    case 'moc_submitted':
      fieldUpdates.mocSubmittedAt = timestamp;
      fieldUpdates.mocSubmittedBy = actorName;
      break;
    case 'moc_approved_by_ops_head':
      fieldUpdates.mocApprovedAt = timestamp;
      fieldUpdates.mocApprovedBy = actorName;
      break;
    case 'moc_rejected_by_ops_head':
      fieldUpdates.mocRejectedAt = timestamp;
      fieldUpdates.mocRejectedBy = actorName;
      break;
    case 'finance_updated':
      fieldUpdates.financeUpdatedAt = timestamp;
      fieldUpdates.financeUpdatedBy = actorName;
      break;
    case 'finance_approved':
      fieldUpdates.financeApprovedAt = timestamp;
      fieldUpdates.financeApprovedBy = actorName;
      break;
    case 'finance_rejected':
      fieldUpdates.financeRejectedAt = timestamp;
      fieldUpdates.financeRejectedBy = actorName;
      break;
    case 'finance_hold':
      fieldUpdates.financeHoldAt = timestamp;
      fieldUpdates.financeHoldBy = actorName;
      break;
    case 'packing_queued':
      fieldUpdates.packingQueuedAt = timestamp;
      fieldUpdates.packingQueuedBy = actorName;
      break;
    case 'packing_scheduled':
    case 'packing_schedule_updated':
      fieldUpdates.packingScheduledAt = timestamp;
      fieldUpdates.packingScheduledBy = actorName;
      if (historyEvent?.dispatchStatus === 'dc_pending' && !existingAudit.dcCreatedAt) {
        fieldUpdates.dcCreatedAt = timestamp;
        fieldUpdates.dcCreatedBy = actorName;
      }
      break;
    case 'packed_ready':
      fieldUpdates.packedReadyAt = timestamp;
      fieldUpdates.packedReadyBy = actorName;
      break;
    case 'dc_created':
      fieldUpdates.dcCreatedAt = timestamp;
      fieldUpdates.dcCreatedBy = actorName;
      break;
    case 'dc_updated':
      fieldUpdates.dcUpdatedAt = timestamp;
      fieldUpdates.dcUpdatedBy = actorName;
      break;
    case 'dc_submitted':
      fieldUpdates.dcSubmittedAt = timestamp;
      fieldUpdates.dcSubmittedBy = actorName;
      break;
    case 'invoice_submitted':
      if (!existingAudit.dcCreatedAt) {
        fieldUpdates.dcCreatedAt = timestamp;
        fieldUpdates.dcCreatedBy = actorName;
      }
      if (!existingAudit.dcSubmittedAt) {
        fieldUpdates.dcSubmittedAt = timestamp;
        fieldUpdates.dcSubmittedBy = actorName;
      }
      fieldUpdates.invoiceSubmittedAt = timestamp;
      fieldUpdates.invoiceSubmittedBy = actorName;
      break;
    case 'qc_updated':
      fieldUpdates.qcUpdatedAt = timestamp;
      fieldUpdates.qcUpdatedBy = actorName;
      break;
    case 'dispatch_confirmed':
      fieldUpdates.dispatchConfirmedAt = timestamp;
      fieldUpdates.dispatchConfirmedBy = actorName;
      break;
    case 'delivery_confirmed':
      fieldUpdates.deliveryConfirmedAt = timestamp;
      fieldUpdates.deliveryConfirmedBy = actorName;
      break;
    default:
      break;
  }

  await updateDoc(auditRef, fieldUpdates);
};
